"use client";

import { getPrescriptions } from "@/api/saleor/services";
import {
  lensConfigurationSteps,
  sessionFlowConstants,
  sessionQueryParams,
} from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import { ConfiguratorStepProps, LensTypeData, LensTypeOptions } from "@/common/lib/types";
import { useUpdateWindowSearchParams } from "@/hooks/useWindowSearchParams";
import { brandId } from "@/runtimeConfig/server";
import { useGlobalStore } from "@/store/store";
import { getCountryCodeFromLocale, getLangFromLocale } from "@/utils/common";
import { useEffect, useMemo } from "react";
import { useConfiguratorContext } from "../../context/Provider";
import { lensTypes } from "../../resource/config";
import SummaryReview from "../../summaryReview/SummaryReview";
import LensTypeCard from "./LensTypeCard";

interface LensTypeStepProps extends ConfiguratorStepProps {
  stepOptions: LensTypeOptions;
  onCloseLensConfigurator?: () => void;
}

const LensType = ({
  locale,
  currencyCode,
  translations,
  handleStepData,
  handleAddToCart,
  onCloseLensConfigurator,
  stepOptions: { isWellFormedConfig, data, ...restStepOptions },
}: LensTypeStepProps) => {
  const updateSearchParam = useUpdateWindowSearchParams();
  const { updateLensType, updateExcludePrescription } = useConfiguratorContext();

  const lang = getLangFromLocale(locale);
  const currentStep = lensConfigurationSteps.LENS_TYPE;
  const channel = `${brandId}-${getCountryCodeFromLocale(locale)}-web`;

  const { user, setHasPrescription } = useGlobalStore();
  const loggedInUser = useMemo(() => !!user?.email, [user]);

  const handleSignInClick = () => {
    const currentHref = window.location.href || "/";
    const hrefUri = new URL(currentHref);
    hrefUri.searchParams.delete(sessionQueryParams.REDIRECT);
    hrefUri.searchParams.delete(sessionQueryParams.LOGIN_MODAL_STATUS);
    hrefUri.searchParams.set(sessionQueryParams.LENSE_SELECTOR, "true");
    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: sessionFlowConstants.LOGIN_EMAIL },
      { key: sessionQueryParams.DISABLE_REDIRECT, value: "true" },
      { key: sessionQueryParams.REDIRECT, value: encodeURIComponent(hrefUri.toString()) },
    ]);
  };

  const handleSelectLensOption = async (data: LensTypeData) => {
    startPageTransition(false);
    await handleStepData(currentStep, data);
    endPageTransition();
  };

  useEffect(() => {
    updateLensType(lensTypes.SINGLE_VISION);
  }, [updateLensType]);

  useEffect(() => {
    if (!!user?.email || !!user?.name) {
      getPrescriptions(1).then((response) => {
        const hasPrescriptions = response?.edges?.length > 0;
        if (hasPrescriptions) {
          setHasPrescription(hasPrescriptions);
          updateExcludePrescription(!hasPrescriptions);
        }
      });
    }
  }, [user, setHasPrescription, updateExcludePrescription, channel]);

  if (!isWellFormedConfig) return <p className="text-sm">Not Configured</p>;
  return (
    <div>
      {!loggedInUser && (
        <div className="mb-6 bg-primary-2 p-2 md:p-4 mx-4">
          <p className="text-sm">
            {translations?.lensConfigurator?.lens_config_lens_options_sign_in_button_description}
            <button
              className="ml-[2px] font-bold underline underline-offset-2"
              onClick={handleSignInClick}
            >
              {translations?.lensConfigurator?.lens_config_lens_options_sign_in_button_title}
            </button>
          </p>
        </div>
      )}
      <p className="mb-6 text-sm font-semibold uppercase px-4">
        {translations?.lensConfigurator?.lens_config_lens_options_section_title?.toUpperCase()}
      </p>
      <div className="grid grid-cols-12 gap-6 px-4">
        {data.map((lens, i) => (
          <LensTypeCard
            key={i}
            lang={lang}
            lensData={lens}
            currencyCode={currencyCode}
            translations={translations}
            onAddToCart={handleAddToCart}
            onCloseLensConfigurator={onCloseLensConfigurator}
            onSelect={() => handleSelectLensOption(lens)}
            className="flex h-full flex-col"
          />
        ))}
      </div>
      <SummaryReview locale={locale} currencyCode={currencyCode} translations={translations} />
    </div>
  );
};

export default LensType;
